import * as React from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CodeValue,
  DescriptionList,
  type DescriptionListItem,
  FormField,
  Input,
  notify,
  Spinner,
} from "Components/ui";
import {
  Eye,
  Gauge,
  Globe,
  KeyRound,
  Plug,
  PlugZap,
  RefreshCw,
  RotateCw,
  Unplug,
} from "Components/ui/icons";

import { getApiErrorMessage, getApiErrorReason } from "api/apiError";
import { EnvBlock, LinkTestResult, LockSwitch, TenantLinkStatus } from "api/teleopdLink";
import { formatDateTime, formatRelative, humanize } from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import { ModeChip } from "Routes/Billing/Components/StatusChips";
import { useLinkActions } from "../data";
import { BillingPermissions } from "../types";
import EnvBlockModal, { EnvBlockContext } from "./EnvBlockModal";
import LinkSettingsModal, { baseUrlError } from "./LinkSettingsModal";
import ShadowStatsCard from "./ShadowStatsCard";

const AUTH_FAILURE_LABELS: Record<string, string> = {
  unknown_key: "Unknown key id (SAP_KEY_ID doesn't match)",
  bad_signature: "Bad signature (SAP_KEY_SECRET is wrong)",
  clock_skew: "Clock skew (TeleOpd's clock is more than 5 minutes off)",
  disabled: "The link is disabled",
  version_retired: "Old key version (rotated and expired)",
};

const NUDGE_REASONS: Record<string, string> = {
  not_configured: "SAP_LINK_MASTER_KEY is not set on SAP",
  invalid_tenant: "unknown client",
  not_linked: "TeleOpd isn't connected",
  disabled: "the link is disabled",
  no_url: "no TeleOpd address is set",
  timeout: "TeleOpd didn't answer in time",
  address_forbidden: "the address is on a private network",
  remote_auth_failed: "TeleOpd rejected SAP's signature",
  not_found: "TeleOpd doesn't have the SAP link module",
  error: "unexpected error",
};

const LOCK_SWITCH_TEXT: Record<LockSwitch, string> = {
  off: "Off: TeleOpd never blocks writes.",
  shadow: "Shadow: TeleOpd only counts what it would block.",
  on: "On: TeleOpd enforces read-only when billing says so.",
};

const CAPABILITY_LABELS: Record<string, string> = {
  readonly_guard_v1: "Read-only guard",
  modules_overlay_v1: "Module overlay",
};

type ConnectionDialog = "reveal" | "rotate" | "disable" | "settings" | null;

type TestState =
  | { ok: true; result: LinkTestResult }
  | { ok: false; message: string; reason: string | null }
  | null;

interface ConnectionTabProps {
  tenantId: string;
  link: TenantLinkStatus | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  permissions: BillingPermissions;
}

const ConnectForm: React.FC<{
  link: TenantLinkStatus;
  canConfigure: boolean;
  onConnect: (input: { baseUrl?: string | null; allowPrivateNetwork?: boolean }) => Promise<void>;
}> = ({ link, canConfigure, onConnect }) => {
  const [baseUrl, setBaseUrl] = React.useState<string>("");
  const [allowPrivate, setAllowPrivate] = React.useState<boolean>(false);
  const [connecting, setConnecting] = React.useState<boolean>(false);
  const urlError = baseUrlError(baseUrl);
  const disabled = !canConfigure || !link.configured;

  const handleConnect = async () => {
    if (urlError) {
      return;
    }
    setConnecting(true);
    try {
      await onConnect({
        ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
        ...(allowPrivate ? { allowPrivateNetwork: true } : {}),
      });
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Connect TeleOpd"
        description="Creates keys for this client. Nothing changes in TeleOpd until you add them to its environment and restart it."
      />
      <CardBody className="space-y-4">
        <FormField
          label="TeleOpd address (optional)"
          id="connect-base-url"
          error={urlError || undefined}
          hint={
            link.tenantUri ? (
              <>
                Leave empty to use the client&apos;s URI (
                <span className="font-mono">{link.tenantUri}</span>).
              </>
            ) : (
              "This client has no URI on record: enter the TeleOpd address so SAP can reach it."
            )
          }
        >
          <Input
            type="url"
            value={baseUrl}
            disabled={disabled}
            placeholder={link.tenantUri || "https://client.example.com"}
            onChange={(event) => setBaseUrl(event.target.value)}
            className="font-mono"
          />
        </FormField>
        <Checkbox
          checked={allowPrivate}
          disabled={disabled}
          onCheckedChange={(checked) => setAllowPrivate(checked === true)}
          label="Allow private network"
          description="Only for deployments inside your own network (10.x, 192.168.x, localhost)."
        />
        <Button
          onClick={handleConnect}
          loading={connecting}
          disabled={disabled || Boolean(urlError)}
          leftIcon={<Plug aria-hidden="true" />}
          title={!canConfigure ? "Needs billing configure access" : undefined}
        >
          Connect TeleOpd
        </Button>
        {!canConfigure && (
          <p className="text-xs text-muted-foreground">
            Connecting needs billing configure access.
          </p>
        )}
      </CardBody>
    </Card>
  );
};

/** TeleOpd link: connect, keys, health, test / sync, and the shadow-mode report. */
const ConnectionTab: React.FC<ConnectionTabProps> = ({
  tenantId,
  link,
  loading,
  error,
  onRetry,
  permissions,
}) => {
  const actions = useLinkActions(tenantId);
  const [dialog, setDialog] = React.useState<ConnectionDialog>(null);
  const [envModal, setEnvModal] = React.useState<{
    envBlock: EnvBlock;
    context: EnvBlockContext;
    previousValidUntil?: string | null;
  } | null>(null);
  const [testState, setTestState] = React.useState<TestState>(null);
  const [previousValidDays, setPreviousValidDays] = React.useState<string>("7");

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
        <Spinner className="h-5 w-5" />
        Loading the TeleOpd connection…
      </div>
    );
  }
  if (error || !link) {
    return (
      <Alert
        tone="danger"
        title="Connection status didn't load"
        action={
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error)}
      </Alert>
    );
  }

  const canConfigure = permissions.canConfigure;
  const keysAvailable = canConfigure && link.configured;

  const runTest = () => {
    setTestState(null);
    actions.test.mutate(undefined, {
      onSuccess: (result) => {
        setTestState({ ok: true, result });
        if (result.ok) {
          notify.success(`TeleOpd answered in ${result.latencyMs} ms`);
        } else {
          notify.error("TeleOpd answered, but didn't report OK");
        }
      },
      onError: (err) => {
        const message = getApiErrorMessage(err);
        setTestState({ ok: false, message, reason: getApiErrorReason(err) });
        notify.error(err);
      },
    });
  };

  const runSyncNow = () => {
    actions.syncNow.mutate(undefined, {
      onSuccess: (result) => {
        if (result.delivered) {
          notify.success("TeleOpd is pulling a fresh snapshot.");
        } else {
          notify.error(
            `Couldn't nudge TeleOpd: ${
              (result.reason && NUDGE_REASONS[result.reason]) ||
              humanize(result.reason) ||
              "unknown reason"
            }. It still syncs on its own every few minutes.`
          );
        }
      },
    });
  };

  const enableLink = () => {
    actions.update.mutate({ enabled: true }, { onError: (err) => notify.error(err) });
  };

  const rotateDays = Number(previousValidDays);
  const rotateDaysError =
    /^\d+$/.test(previousValidDays.trim()) && rotateDays >= 1 && rotateDays <= 30
      ? null
      : "Use 1 to 30 days.";

  const statusItems: DescriptionListItem[] = link.linked
    ? [
        {
          label: "Link",
          value: link.enabled ? "Enabled" : "Disabled",
          hint: link.createdAt ? `Connected ${formatDateTime(link.createdAt)}` : undefined,
        },
        {
          label: "TeleOpd address",
          value: link.baseUrl || "Not set",
          mono: Boolean(link.baseUrl),
          hint:
            link.baseUrlSource === "link"
              ? "Override set on this link"
              : link.baseUrlSource === "tenant"
              ? "From the client's URI"
              : "Set an address to test and nudge TeleOpd",
        },
        {
          label: "Key",
          value: link.keyId ? <CodeValue value={link.keyId} label="key id" /> : null,
          hint: `Version ${link.version}${
            link.lastSeenVersion ? ` · TeleOpd last used v${link.lastSeenVersion}` : ""
          }`,
        },
        {
          label: "Rotated",
          value: link.rotatedAt ? formatDateTime(link.rotatedAt) : "Never",
          hint:
            link.previousKeyActive && link.previousValidUntil
              ? `Previous key works until ${formatDateTime(link.previousValidUntil)}`
              : undefined,
        },
        {
          label: "Last sync",
          value: link.lastSyncAt ? formatRelative(link.lastSyncAt) : "Never",
          hint: link.lastSyncAt
            ? `${formatDateTime(link.lastSyncAt)}${
                link.lastSyncIp ? ` · from ${link.lastSyncIp}` : ""
              }`
            : "Waiting for TeleOpd's first sync",
        },
        {
          label: "TeleOpd version",
          value: link.appVersion || "Unknown",
          hint: link.appUri ? `Reports address ${link.appUri}` : undefined,
        },
        {
          label: "Lock switch",
          value: link.lockSwitch ? link.lockSwitch.toUpperCase() : "Unknown",
          hint: link.lockSwitch ? LOCK_SWITCH_TEXT[link.lockSwitch] : "Reported on the next sync",
        },
        {
          label: "Capabilities",
          value:
            link.capabilities && link.capabilities.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {link.capabilities.map((capability) => (
                  <Badge key={capability} tone="neutral">
                    {CAPABILITY_LABELS[capability] || capability}
                  </Badge>
                ))}
              </span>
            ) : (
              "None reported"
            ),
          hint: link.readOnlyCapable
            ? "Read-only enforcement available"
            : "Read-only not available",
        },
        {
          label: "Snapshot",
          value:
            link.snapshotAdopted === true
              ? "Adopted by TeleOpd"
              : link.snapshotAdopted === false
              ? "Not adopted yet"
              : "Unknown",
          hint: link.lastSnapshotAt
            ? `Last sent ${formatRelative(link.lastSnapshotAt)}`
            : undefined,
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      {!link.configured && (
        <Alert tone="danger" title="SAP_LINK_MASTER_KEY is not set on the SAP server">
          SAP derives every client's TeleOpd keys from this master key. Ask an engineer to add a
          random secret of at least 32 bytes as SAP_LINK_MASTER_KEY to the SAP server's environment
          and restart SAP. Until then clients can't be connected and existing links can't sync.
        </Alert>
      )}

      {!link.linked ? (
        <ConnectForm
          link={link}
          canConfigure={canConfigure}
          onConnect={async (input) => {
            try {
              const result = await actions.connect.mutateAsync(input);
              setEnvModal({ envBlock: result.envBlock, context: "connect" });
            } catch (err) {
              // The mutation already showed the error.
            }
          }}
        />
      ) : (
        <>
          {!link.enabled && (
            <Alert
              tone="warning"
              title="This link is disabled"
              action={
                canConfigure ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={enableLink}
                    loading={actions.update.isLoading}
                    leftIcon={<PlugZap aria-hidden="true" />}
                  >
                    Enable
                  </Button>
                ) : undefined
              }
            >
              SAP rejects TeleOpd's syncs, and TeleOpd keeps acting on the last snapshot it
              received.
            </Alert>
          )}
          {link.enabled && link.stale && (
            <Alert
              tone="warning"
              title={link.lastSyncAt ? "TeleOpd stopped syncing" : "Waiting for the first sync"}
            >
              {link.lastSyncAt
                ? `No sync for over an hour (last ${formatRelative(
                    link.lastSyncAt
                  )}). Check that TeleOpd is running and can reach SAP.`
                : "TeleOpd hasn't synced yet. Check the SAP_* variables on the deployment and restart it."}
            </Alert>
          )}
          {link.appUriMismatch && (
            <Alert tone="warning" title="TeleOpd reports a different address">
              TeleOpd says it runs at <span className="font-mono">{link.appUri}</span>, but SAP
              expects{" "}
              {link.baseUrl ? <span className="font-mono">{link.baseUrl}</span> : "another address"}
              . A staging copy may be using this client's keys.
            </Alert>
          )}
          {link.lastAuthFailure && (
            <Alert tone="danger" title="Last rejected request from TeleOpd">
              {(link.lastAuthFailure.reason && AUTH_FAILURE_LABELS[link.lastAuthFailure.reason]) ||
                humanize(link.lastAuthFailure.reason) ||
                "Unknown reason"}{" "}
              · {formatRelative(link.lastAuthFailure.at)} ({formatDateTime(link.lastAuthFailure.at)}
              ){link.lastAuthFailure.ip ? ` · from ${link.lastAuthFailure.ip}` : ""}
            </Alert>
          )}

          <Card>
            <CardHeader title="Status" description="As reported by TeleOpd on its last sync." />
            <CardBody>
              <DescriptionList columns={3} items={statusItems} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Actions"
              description="Test and nudge need the TeleOpd address and a running deployment."
            />
            <CardBody>
              <div className="flex flex-wrap gap-2">
                {canConfigure && (
                  <Button
                    variant="secondary"
                    onClick={runTest}
                    loading={actions.test.isLoading}
                    disabled={!link.enabled || !link.configured}
                    leftIcon={<Gauge aria-hidden="true" />}
                  >
                    Test connection
                  </Button>
                )}
                {permissions.canSync && (
                  <Button
                    variant="secondary"
                    onClick={runSyncNow}
                    loading={actions.syncNow.isLoading}
                    disabled={!link.enabled}
                    leftIcon={<RefreshCw aria-hidden="true" />}
                  >
                    Sync now
                  </Button>
                )}
                {canConfigure && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setDialog("reveal")}
                      disabled={!keysAvailable}
                      leftIcon={<Eye aria-hidden="true" />}
                    >
                      Reveal keys
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setDialog("settings")}
                      leftIcon={<Globe aria-hidden="true" />}
                    >
                      Edit address
                    </Button>
                    <Button
                      variant="danger-outline"
                      onClick={() => setDialog("rotate")}
                      disabled={!keysAvailable}
                      leftIcon={<RotateCw aria-hidden="true" />}
                    >
                      Rotate keys
                    </Button>
                    {link.enabled ? (
                      <Button
                        variant="danger-outline"
                        onClick={() => setDialog("disable")}
                        leftIcon={<Unplug aria-hidden="true" />}
                      >
                        Disable link
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={enableLink}
                        loading={actions.update.isLoading}
                        leftIcon={<PlugZap aria-hidden="true" />}
                      >
                        Enable link
                      </Button>
                    )}
                  </>
                )}
              </div>
              {!canConfigure && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Testing, keys and address changes need billing configure access.
                </p>
              )}
              {testState && testState.ok && (
                <Alert
                  tone={testState.result.ok ? "success" : "warning"}
                  title={testState.result.ok ? "TeleOpd answered" : "TeleOpd answered, but not OK"}
                  className="mt-4"
                >
                  <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{testState.result.latencyMs} ms</span>
                    <span>· version {testState.result.appVersion || "unknown"}</span>
                    <span>· lock switch {testState.result.lockSwitch || "unknown"}</span>
                    {testState.result.mode && (
                      <span className="inline-flex items-center gap-1">
                        · mode <ModeChip mode={testState.result.mode} />
                      </span>
                    )}
                    {testState.result.snapshotGeneratedAt && (
                      <span>
                        · snapshot from {formatRelative(testState.result.snapshotGeneratedAt)}
                      </span>
                    )}
                  </span>
                </Alert>
              )}
              {testState && !testState.ok && (
                <Alert tone="danger" title="Test failed" className="mt-4">
                  {testState.message}
                  {testState.reason ? (
                    <span className="font-mono text-xs"> ({testState.reason})</span>
                  ) : null}
                </Alert>
              )}
            </CardBody>
          </Card>

          <ShadowStatsCard stats={link.shadowStats} lockSwitch={link.lockSwitch} />
        </>
      )}

      {dialog === "reveal" && (
        <ConfirmDialog
          title="Show the TeleOpd keys?"
          description="Use this when setting up or repairing the TeleOpd deployment. Revealing is recorded in the audit log."
          confirmLabel="Reveal keys"
          onConfirm={async () => {
            const result = await actions.reveal.mutateAsync();
            setEnvModal({ envBlock: result.envBlock, context: "reveal" });
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "rotate" && (
        <ConfirmDialog
          title="Rotate keys?"
          description={`Creates key version ${
            (link.version || 0) + 1
          }. Update TeleOpd with the new values; the current key keeps working for the days below, then TeleOpd can't sync until it is updated.`}
          confirmLabel="Rotate keys"
          tone="danger"
          blockingError={rotateDaysError}
          onConfirm={async () => {
            const result = await actions.rotate.mutateAsync(rotateDays);
            setEnvModal({
              envBlock: result.envBlock,
              context: "rotate",
              previousValidUntil: result.status.previousValidUntil,
            });
          }}
          onClose={() => setDialog(null)}
        >
          <FormField
            label="Keep the current key valid for (days)"
            id="rotate-days"
            error={rotateDaysError || undefined}
            hint="1 to 30 days. Default 7."
          >
            <Input
              type="text"
              inputMode="numeric"
              value={previousValidDays}
              onChange={(event) => setPreviousValidDays(event.target.value)}
              wrapperClassName="w-24"
              className="tabular-nums"
            />
          </FormField>
        </ConfirmDialog>
      )}
      {dialog === "disable" && (
        <ConfirmDialog
          title="Disable the TeleOpd link?"
          description="SAP stops accepting TeleOpd's syncs and stops nudging it. TeleOpd keeps acting on its last snapshot, so SAP refuses while read-only enforcement is active."
          confirmLabel="Disable link"
          tone="danger"
          showErrorInline
          onConfirm={() => actions.update.mutateAsync({ enabled: false })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "settings" && (
        <LinkSettingsModal
          link={link}
          onSave={(input) => actions.update.mutateAsync(input)}
          onClose={() => setDialog(null)}
        />
      )}
      {envModal && (
        <EnvBlockModal
          envBlock={envModal.envBlock}
          context={envModal.context}
          previousValidUntil={envModal.previousValidUntil}
          onClose={() => setEnvModal(null)}
        />
      )}
      {link.linked && link.rolesSnapshot && (
        <p className="text-xs text-subtle-foreground">
          <KeyRound className="mr-1 inline h-3 w-3" aria-hidden="true" />
          TeleOpd reported {link.rolesSnapshot.count} roles ({link.rolesSnapshot.fullAccessCount}{" "}
          with full access)
          {link.rolesSnapshot.at ? ` · ${formatRelative(link.rolesSnapshot.at)}` : ""}.
        </p>
      )}
    </div>
  );
};

export default ConnectionTab;
